// 本地"打印代理"（仓库电脑上的 print-agent，默认 http://127.0.0.1:9135）：收到 PDF 后按任务类型/标签尺寸自动选对应的标签打印机。
// 代理不可用时退回浏览器打印。注意：代理收到任务就返回成功，之后打印机离线、缺纸等情况页面无法得知。

export type PrintChannel = 'agent' | 'browser';

export interface PrintPdfOptions {
  /** 代理按它（其次按标签尺寸）选打印机 */
  jobType: string;
  /** 标签尺寸（英寸） */
  labelSize: { wIn: number; hIn: number };
  fileName?: string;
  code?: string;
  /** 交给代理的份数（默认 1；需要多份时建议直接在 PDF 里重复页面） */
  copies?: number;
}

export interface PrintPdfResult {
  channel: PrintChannel;
  jobId?: string;
  /** 走了浏览器打印时，说明代理为什么没用上 */
  agentError?: string;
}

export function getPrintAgentUrl(): string {
  try {
    const custom = String(window.localStorage.getItem('XT_PRINT_AGENT_URL') || '').trim();
    if (custom) return custom;
  } catch { /* 无法读取本地存储时用默认值 */ }
  try {
    const env = String((process as any)?.env?.REACT_APP_PRINT_AGENT_URL || '').trim();
    if (env) return env;
  } catch { /* ignore */ }
  return 'http://127.0.0.1:9135';
}

const agentBase = () => getPrintAgentUrl().replace(/\/$/, '');

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.onload = () => {
      const raw = String(reader.result || '');
      const index = raw.indexOf(',');
      resolve(index >= 0 ? raw.slice(index + 1) : raw);
    };
    reader.readAsDataURL(blob);
  });
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => { try { controller.abort(); } catch { /* ignore */ } }, timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** 打印代理是否可用（很短的超时，不让页面等待） */
export async function isPrintAgentReachable(timeoutMs = 1500): Promise<boolean> {
  try {
    const resp = await fetchWithTimeout(`${agentBase()}/health`, { method: 'GET' }, timeoutMs);
    return resp.ok;
  } catch {
    return false;
  }
}

export async function sendPdfToPrintAgent(blob: Blob, options: PrintPdfOptions): Promise<{ ok: boolean; jobId?: string; error?: string }> {
  try {
    const content = await blobToBase64(blob);
    const copies = Math.min(50, Math.max(1, Math.floor(Number(options.copies) || 1)));
    const resp = await fetchWithTimeout(`${agentBase()}/api/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_type: options.jobType,
        label_size_in: { w: options.labelSize.wIn, h: options.labelSize.hIn },
        copies,
        file_name: options.fileName || '',
        code: options.code || '',
        content_base64: content,
        content_type: 'application/pdf',
        hint: { source: 'xt01b-web', ts: Date.now() },
      }),
    }, 8000);
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      return { ok: false, error: `HTTP ${resp.status} ${text}`.trim() };
    }
    const data: any = await resp.json().catch(() => ({}));
    return { ok: true, jobId: String(data?.job_id || data?.id || '') || undefined };
  } catch (err: any) {
    return { ok: false, error: err?.message || '打印代理无法连接' };
  }
}

/** 浏览器打印：用隐藏 iframe 加载 PDF 后调用打印，不弹新窗口；打印对话框里需要自己选标签打印机 */
export function printPdfInBrowser(blob: Blob): void {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  const cleanup = () => {
    try { document.body.removeChild(iframe); } catch { /* ignore */ }
    try { URL.revokeObjectURL(url); } catch { /* ignore */ }
  };
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      // 这个浏览器不允许从 iframe 打印：改成在新标签页打开 PDF，由用户自己点打印
      window.open(url, '_blank');
      return;
    }
    // 延迟清理，避免打印对话框打开前 iframe 就被移除
    setTimeout(cleanup, 60000);
  };
  iframe.src = url;
  document.body.appendChild(iframe);
}

/** 先试打印代理，不可用就退回浏览器打印。两条路都失败（极少见）才抛错。 */
export async function printPdfBlob(blob: Blob, options: PrintPdfOptions): Promise<PrintPdfResult> {
  let agentError = '';
  if (await isPrintAgentReachable()) {
    const sent = await sendPdfToPrintAgent(blob, options);
    if (sent.ok) return { channel: 'agent', jobId: sent.jobId };
    agentError = sent.error || '打印代理返回错误';
  } else {
    agentError = '打印代理无法连接';
  }
  printPdfInBrowser(blob);
  return { channel: 'browser', agentError };
}
